import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHandle,
  BottomSheetOverlay,
  Button,
  NeoColor,
  P3,
  Stack,
  ThemeProvider,
  neoTheme,
} from "@noom/wax-component-library";

import type { TreatmentOverview } from "./hooks/useTreatmentOverviews";
import { renderProgramName } from "./renderProgramName";
import { TreatmentProductLockup } from "./TreatmentProductLockup";

export type TreatmentBottomSheetCtaVariant = "primary" | "secondary";

type TreatmentDetailBottomSheetProps = {
  treatment: TreatmentOverview | undefined;
  isOpen: boolean;
  onClose: () => void;
  body: string | undefined;
  ctaLabel: string | undefined;
  ctaVariant: TreatmentBottomSheetCtaVariant | undefined;
  ctaHref?: string;
  showProgramName?: boolean;
  onConfirm: () => void;
};

type WaxButtonVariantConfig = {
  variant: "solid" | "outline";
  colorScheme: "primary" | "secondary";
};

const CTA_VARIANT_TO_WAX: Record<
  TreatmentBottomSheetCtaVariant,
  WaxButtonVariantConfig
> = {
  primary: { variant: "solid", colorScheme: "primary" },
  secondary: { variant: "solid", colorScheme: "secondary" },
};

export const TreatmentDetailBottomSheet = ({
  treatment,
  isOpen,
  onClose,
  body,
  ctaLabel,
  ctaVariant,
  ctaHref,
  showProgramName = true,
  onConfirm,
}: TreatmentDetailBottomSheetProps) => {
  const eyebrow =
    showProgramName && treatment?.formattedProgramName
      ? renderProgramName(treatment.formattedProgramName)
      : undefined;
  const ctaAnchorProps = ctaHref
    ? {
        as: "a" as const,
        href: ctaHref,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <ThemeProvider theme={neoTheme}>
      <BottomSheet isOpen={isOpen} onClose={onClose}>
        <BottomSheetOverlay />
        <BottomSheetContent
          data-cy="treatment-sheet"
          sx={{
            borderRadius: "16px 16px 0 0 !important",
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
            borderBottomRadius: "0 !important",
          }}
        >
          <BottomSheetHandle enableSwipeToClose onSwipeClose={onClose} />
          <BottomSheetBody>
            <Stack spacing={4}>
              {treatment && (
                <TreatmentProductLockup
                  eyebrow={eyebrow}
                  title={treatment.treatmentName}
                  imageUrl={treatment.productIconUrl ?? treatment.productImageUrl}
                  imageAlt={treatment.treatmentName}
                  productBackgroundColor={treatment.productBackgroundColor}
                />
              )}
              {body && <P3 color={NeoColor.greyscale.black}>{body}</P3>}
            </Stack>
          </BottomSheetBody>
          <BottomSheetFooter>
            {ctaLabel && (
              <Button
                data-cy="treatment-sheet-cta"
                size="lg"
                isFullWidth
                {...(ctaVariant
                  ? CTA_VARIANT_TO_WAX[ctaVariant]
                  : CTA_VARIANT_TO_WAX.primary)}
                {...ctaAnchorProps}
                onClick={() => onConfirm()}
              >
                {ctaLabel}
              </Button>
            )}
          </BottomSheetFooter>
        </BottomSheetContent>
      </BottomSheet>
    </ThemeProvider>
  );
};
